import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwConfigServiceToken, DwConfigService, DwSecurityUserService, DwModalResult, DwModalService, DwModalConfig, DwModalButtonStandardConfigs, DwModalSize, DwEventService } from "@devwareapps/devware-cap";
import { CheckridePaymentRequest } from "../models/checkride-payment-request.model";
import { Observable } from "rxjs";
import { CheckridePaymentResult } from "../models/checkride-payment-result.model";
import { AppMetaData, AppMetaDataItemNames, CheckRidePaymentEntity, CheckRideRequestEntity, CheckRideRequestSubStatusAllItems, PaymentCardEntity, PaymentCustomerReferenceEntity } from "../../../meta-data/app-meta-data.service";
import { PaymentModalComponent } from "../components/payment-modal/payment-modal.component";
import { PaymentModalOptions } from "../models/payment-modal-options.model";
import { CheckrideRefundPaymentResult } from "../models/checkride-refund-payment-result.model";
import { CheckrideRefundPaymentRequest } from "../models/checkride-refund-payment-request.model";
import { PaymentDiscountResult } from "../models/payment-discount-result.model";
import { PaymentAddCardRequest } from "../models/payment-add-card-request.model";
import { PaymentAddCardResult } from "../models/payment-add-card-result.model";
import { PaymentChargeRequest } from "../models/payment-charge-request.model";
import { CheckrideSchoolPaymentRequest } from "../models/checkride-school-payment-request.model";
import { CheckrideSchoolPaymentResult } from "../models/checkride-school-payment-result.model";
import { tap } from "rxjs/operators";


@Injectable({ providedIn: 'root' })
export class PaymentRepositoryService {

    apiRoot: string;

    constructor(
        private http: HttpClient,
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwModalService: DwModalService,
        private dwEventService: DwEventService,
    ) {
        this.apiRoot = configService.coreConfig.apiRoot;
    }

    /**
     * Creates a Checkride payment
     * @param checkridePaymentRequest Payment Request
     * @returns Checkride payment result indicating success/failure
     */
    public createCheckridePayment(checkridePaymentRequest: CheckridePaymentRequest): Observable<CheckridePaymentResult> {
        return this.http.post<CheckridePaymentResult>(`${this.apiRoot}/pilexos/payments/create`, checkridePaymentRequest);
    }

    createSchoolCheckridePayment(checkride: CheckRideRequestEntity, chargeRequest: PaymentChargeRequest): Observable<CheckrideSchoolPaymentResult> {
        const request: CheckrideSchoolPaymentRequest = {
            CheckRideRequestJSON: JSON.stringify(checkride),
            chargeRequest: chargeRequest,
            checkRideRequestId: checkride.CheckRideRequestId
        };

        return this.http.post<CheckrideSchoolPaymentResult>(`${this.apiRoot}/pilexos/payments/create-school-payment`, request)
            .pipe(tap(results => {
                this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CheckRideRequest), results);
            }));
    }

    /**
   * Creates a Checkride payment
   * @param checkridePaymentRequest Payment Request
   * @returns Checkride payment result indicating success/failure
   */
    public addCard(PaymentAddCardRequest: PaymentAddCardRequest): Observable<PaymentAddCardResult> {
        return this.http.post<PaymentAddCardResult>(`${this.apiRoot}/pilexos/payments/add-card`, PaymentAddCardRequest);
    }


    getCheckrideRequest(checkrideRequestId: number): Observable<CheckRideRequestEntity> {
        const checkRideQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        checkRideQuery.addFilterAnd(filters => filters.Equal(field => field.CheckRideRequestId, checkrideRequestId));

        return this.dwOrmDataService.executeQuerySingle(checkRideQuery);
    }
    /**
     * Admin function to refund a checkride payment
     * @param refundRequest 
     * @returns CheckrideRefundPaymentResult
     */
    refundCheckridePayment(refundRequeset: CheckrideRefundPaymentRequest): Observable<CheckrideRefundPaymentResult> {

        return this.http.post<CheckridePaymentResult>(`${this.apiRoot}/pilexos/payments/refund`, refundRequeset)
            .pipe(tap(results => {
                this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CheckRideRequest), results);
            }));
    }

    /**
     * Apply a discount code to a payment 
     * @remarks This is only used to show the user the discounted amount, but the logic is reapplied on the server side
     * @param discountCode Discount code provided by the user
     * @param totalAmount Total amount before discount
     * @returns Payment Discount result indicating if the discount is valid and the resulting amount
     */
    applyDiscountCode(discountCode: string, totalAmount: number): Observable<PaymentDiscountResult> {
        return this.http.post<PaymentDiscountResult>(`${this.apiRoot}/pilexos/payments/apply-discount-code?discountCode=${discountCode}&totalAmount=${totalAmount}`, {});
    }

    getCustomer(partyTypeId: number): Observable<PaymentCustomerReferenceEntity> {
        const request = {
            OwnerPartyTypeId: partyTypeId
        };

        return this.http.post<PaymentCustomerReferenceEntity>(`${this.apiRoot}/pilexos/payments/get-customer`, request);
    }

}